import api from 'plugin/api';
import { API_URL } from 'types/const';
import isJSON from 'utils/isJSON';
import jsonToFormData from 'utils/json/toFormData';
import format from 'utils/string/format';

/**
 * 제품 생산량 목록 조회
 * @param {Object} [params] 요청 데이터
 * @param {number[]} [params.factoryCodeSeqList] 선택한 공장
 * @param {string} [params.prodYm] 월
 * @param {0 | 1} [params.searchType=0] 구분 (0: 제품 코드, 1: 배합비 코드)
 * @param {string} [params.prdtCd] 제품 코드
 * @param {string} [params.mixCd] 배합비 코드
 * @return {Object} 응답 데이터
 */
async function requestList(params) {
  return api.get(API_URL.sfms.product.output.default, { params });
}

/**
 * 제품 생산량 등록
 * @param {Object} data 요청 데이터
 * @param {number} data.factoryCodeSeq 공장 코드
 * @param {string} data.pltCode 공장 코드
 * @param {string} data.mixCd 배합비 코드
 * @param {Object[]} data.productOutturnList 제품 목록
 * @param {string} data.productOutturnList[].prdtCd 제품 코드
 * @param {number} data.productOutturnList[].amount 생산량
 * @param {string} data.prodYm 월
 * @return {Object} 응답 데이터
 */
async function requestInsert(data) {
  return api.post(API_URL.sfms.product.output.default, data);
}

/**
 * 제품 생산량 수정
 * @param {number} seq 배합비 코드 번호
 * @param {Object} data 요청 데이터
 * @param {number} data.factoryCodeSeq 공장 코드
 * @param {string} data.pltCode 공장 코드
 * @param {string} data.mixCd 배합비 코드
 * @param {Object[]} data.productOutturnList 제품 목록
 * @param {string} data.productOutturnList[].prdtCd 제품 코드
 * @param {number} data.productOutturnList[].amount 생산량
 * @param {string} data.prodYm 월
 * @return {Object} 응답 데이터
 */
async function requestUpdate(seq, data) {
  return api.put(format(API_URL.sfms.product.output.inform, { seq }), data);
}

/**
 * 제품 생산량 삭제
 * @param {number} seq 배합비 코드 번호
 * @param {Object} data 요청 데이터
 * @param {Object[]} data.productOutturnList 제품 목록
 * @param {number} data.productOutturnList[].seq 제품 번호
 * @return {Object} 응답 데이터
 */
async function requestDelete(seq, data) {
  return api.delete(format(API_URL.sfms.product.output.inform, { seq }), {
    data
  });
}

/**
 * 제품 생산량 다중 삭제
 * @param {Object} data 요청 데이터
 * @param {number[]} data.seqList 제품 생산량 번호 목록
 * @return {Object} 응답 데이터
 */
async function requestDeletes(data) {
  return api.delete(API_URL.sfms.product.output.default, { data });
}

/**
 * 제품 생산량 정보 조회
 * @param {number} seq 배합비 코드 번호
 * @param {Object} params 요청 데이터
 * @param {string} params.prodYm 월
 * @return {Object} 응답 데이터
 */
async function requestInform(seq, params) {
  return api.get(format(API_URL.sfms.product.output.inform, { seq }), {
    params
  });
}

/**
 * 제품 생산량 업로드 폼 다운로드
 * @return {Object} 응답 데이터
 */
async function requestDownload() {
  return api.get(API_URL.sfms.product.output.download, {
    responseType: 'arraybuffer'
  });
}

/**
 * 제품 생산량 업로드
 * @param {Object} data 요청 데이터
 * @param {number} data.factoryCodeSeq 공장
 * @param {string} data.prodYm 월
 * @param {File} data.file xlsx 파일
 * @return {Object} 응답 데이터
 */
async function requestUpload(data) {
  if (isJSON(data)) {
    data = jsonToFormData(data);
  }

  return api.post(API_URL.sfms.product.output.upload, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
}

/**
 * 제품 생산량 SAP에서 가져오기
 * @param {Object} params 요청 데이터
 * @param {string} params.prodYm 월
 * @return {Object} 응답 데이터
 */
async function requestImport(params) {
  return api.get(API_URL.sfms.product.output.import, { params });
}

/**
 * 제품 생산량 초기화
 * @param {Object} params 요청 데이터
 * @param {number} params.factoryCodeSeq 공장
 * @param {string} params.prodYm 월
 * @return {Object} 응답 데이터
 */
async function requestInit(params) {
  return api.delete(API_URL.sfms.product.output.init, { params });
}

export {
  requestList,
  requestInsert,
  requestUpdate,
  requestDelete,
  requestDeletes,
  requestInform,
  requestDownload,
  requestUpload,
  requestImport,
  requestInit
};
